import {
  CarryOutOutlined,
  CheckCircleFilled,
  ClockCircleOutlined,
  ExclamationCircleFilled,
} from '@ant-design/icons';
import cn from 'classnames';
import { memo } from 'react';
import { Link } from 'react-router-dom';

import { Avatar } from '@app/components/Avatar';
import { LEAD_CARD_PATH } from '@app/constants';
import { MAX_VIEW } from '@app/shared/leads/constants/displayLimits';
import { SocialIntegrationIcon } from '@app/shared/ui/SocialIntegrationIcon';
import { Tag } from '@app/shared/ui/Tag';

import { LEADS_REGISTRY_EMPTY_VALUE } from '../../../../model/constants';
import { useLeadResponseTime } from '../../../../model/hooks/useLeadResponseTime';

import type {
  LeadsRegistryLeadRow,
  LeadsRegistryTaskStatus,
} from '../../../../model/types';
import type { FC, ReactNode } from 'react';

interface LeadsRegistryLeadCardProps {
  isCompact: boolean;
  row: LeadsRegistryLeadRow;
}

const TASK_STATUS_LABELS: Record<LeadsRegistryTaskStatus, string> = {
  completed: 'Готово',
  overdue: 'Просрочено',
  planned: 'Задача',
};

const TASK_STATUS_ICONS: Record<LeadsRegistryTaskStatus, ReactNode> = {
  completed: <CheckCircleFilled />,
  overdue: <ExclamationCircleFilled />,
  planned: <ClockCircleOutlined />,
};

const NORMAL_TAGS_COUNT = Math.min(MAX_VIEW, 2);
const COMPACT_TAGS_COUNT = 1;

const LeadsRegistryLeadCardComponent: FC<LeadsRegistryLeadCardProps> = ({
  isCompact,
  row,
}) => {
  const { id, leadInfo, legacyColumns, sourceLead } = row;
  const {
    avatarUrl,
    clientName,
    clientPhone,
    canShowContacts,
    hasUnreadMessage,
    isNoAnswer,
    lastMessage,
    crmTaskMessage,
    lastMessageTime,
    socialIntegrationType,
    socialIntegrationName,
  } = leadInfo;

  // Лид поднят по наступлению задачи: иконка задачи и её текст
  const hasCrmTask = Boolean(crmTaskMessage);
  const { currentTask, firstTouch, funnelStage, responsibleEmployee, tags } =
    legacyColumns;
  const responseTime = useLeadResponseTime(sourceLead);
  const visibleTagsCount = isCompact ? COMPACT_TAGS_COUNT : NORMAL_TAGS_COUNT;
  const visibleTags = tags.slice(0, visibleTagsCount);
  const hiddenTagsCount = tags.length - visibleTags.length;
  const funnelLabel = isCompact
    ? funnelStage.stageName
    : `${funnelStage.funnelName}: ${funnelStage.stageName}`;
  const responsibleLabel = responsibleEmployee?.name ?? 'Без ответственного';
  const compactMetaLabel = `${funnelStage.stageName} • ${responsibleLabel}`;
  const contactLabel =
    clientPhone ??
    socialIntegrationName ??
    firstTouch.label ??
    LEADS_REGISTRY_EMPTY_VALUE;

  return (
    <Link
      className={cn('LeadsRegistryCards__card', {
        // Голубой фон означает «неотвечено», точка — «непрочитано».
        // Классы исторически названы наоборот, переименование — отдельной задачей.
        'LeadsRegistryCards__card--unread': isNoAnswer,
        'LeadsRegistryCards__card--compact': isCompact,
      })}
      to={`${LEAD_CARD_PATH}?leadId=${id}&source=registry`}
    >
      <div className="LeadsRegistryCards__card-head">
        <div className="LeadsRegistryCards__identity">
          <Avatar
            className="LeadsRegistryCards__avatar"
            src={avatarUrl}
            nameFirst={clientName}
            size={32}
            round
          />

          <div className="LeadsRegistryCards__identity-text">
            <div className="LeadsRegistryCards__title-row">
              {hasUnreadMessage && (
                <span
                  className="LeadsRegistryCards__no-answer-dot"
                  aria-hidden="true"
                />
              )}
              <span className="LeadsRegistryCards__title">{clientName}</span>
            </div>
            {canShowContacts && (
              <span className="LeadsRegistryCards__subtitle">
                {contactLabel}
              </span>
            )}
          </div>
        </div>

        <div className="LeadsRegistryCards__status-group">
          {currentTask ? (
            <span
              className={`LeadsRegistryCards__task-badge LeadsRegistryCards__task-badge--${currentTask.status}`}
              title={currentTask.title}
            >
              {TASK_STATUS_ICONS[currentTask.status]}
              <span>{TASK_STATUS_LABELS[currentTask.status]}</span>
            </span>
          ) : (
            responseTime && (
              <span
                className={`LeadsRegistryCards__response LeadsRegistryCards__response--${responseTime.status}`}
              >
                {responseTime.label}
              </span>
            )
          )}
          {!isCompact && lastMessageTime && (
            <span className="LeadsRegistryCards__time">{lastMessageTime}</span>
          )}
        </div>
      </div>

      <div className="LeadsRegistryCards__message-row">
        {hasCrmTask ? (
          <CarryOutOutlined
            className="LeadsRegistryCards__task-icon"
            aria-label="Задача"
          />
        ) : (
          <SocialIntegrationIcon
            type={socialIntegrationType}
            size="sm"
            className="LeadsRegistryCards__integration"
          />
        )}
        <span className="LeadsRegistryCards__message">
          {crmTaskMessage ?? lastMessage}
        </span>
      </div>

      {isCompact ? (
        <span className="LeadsRegistryCards__compact-meta">
          {compactMetaLabel}
        </span>
      ) : (
        <div className="LeadsRegistryCards__meta-grid">
          <span className="LeadsRegistryCards__meta-item">
            <span className="LeadsRegistryCards__meta-label">Этап</span>
            <span className="LeadsRegistryCards__meta-value">
              {funnelLabel}
            </span>
          </span>
          <span className="LeadsRegistryCards__meta-item">
            <span className="LeadsRegistryCards__meta-label">
              Ответственный
            </span>
            <span className="LeadsRegistryCards__meta-value">
              {responsibleLabel}
            </span>
          </span>
          <span className="LeadsRegistryCards__meta-item LeadsRegistryCards__meta-item--secondary">
            <span className="LeadsRegistryCards__meta-label">
              Первое касание
            </span>
            <span className="LeadsRegistryCards__meta-value">
              {firstTouch.label ?? LEADS_REGISTRY_EMPTY_VALUE}
              {firstTouch.dateTime ? ` • ${firstTouch.dateTime}` : ''}
            </span>
          </span>
        </div>
      )}

      {visibleTags.length > 0 && (
        <div className="LeadsRegistryCards__tag-row">
          {visibleTags.map((tag) => (
            <Tag
              className="LeadsRegistryCards__tag"
              item={{
                id: tag.id,
                label: tag.title,
                color: tag.color,
                iconId: tag.iconId ?? undefined,
              }}
              key={tag.id}
            />
          ))}
          {hiddenTagsCount > 0 && (
            <span className="LeadsRegistryCards__tags-more">
              +{hiddenTagsCount}
            </span>
          )}
        </div>
      )}
    </Link>
  );
};

export const LeadsRegistryLeadCard = memo(LeadsRegistryLeadCardComponent);
